import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { StoreSettings, SettingsSection } from '../types/storeSettings';
import { getStoreSettings, updateStoreSettings, initializeStoreSettings } from '../services/storeSettingsService';
import { Banner } from '../components/store/StoreBanner';
import { Promotion } from '../components/store/ProductPromotion';
import { loadStoreSettingsReliably, setupRealtimeStoreListener, cacheStoreData } from '../utils/storeDataLoader';

interface StoreSettingsContextType {
  settings: StoreSettings | null;
  loading: boolean;
  error: string | null;
  updateSettings: (newSettings: StoreSettings) => Promise<void>;
  updateSection: (section: SettingsSection, data: any) => Promise<void>;
  updateBanners: (banners: Banner[]) => Promise<void>;
  updatePromotions: (promotions: Promotion[]) => Promise<void>;
  refreshSettings: () => Promise<StoreSettings>;
}

const StoreSettingsContext = createContext<StoreSettingsContextType | undefined>(undefined);

export const useStoreSettings = (): StoreSettingsContextType => {
  const context = useContext(StoreSettingsContext);
  if (!context) {
    throw new Error('useStoreSettings must be used within a StoreSettingsProvider');
  }
  return context;
};

interface StoreSettingsProviderProps {
  children: ReactNode;
}

export const StoreSettingsProvider: React.FC<StoreSettingsProviderProps> = ({ children }) => {
  console.log('StoreSettingsProvider initialized');
  const [settings, setSettings] = useState<StoreSettings | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // طباعة الإعدادات عند تغييرها
  useEffect(() => {
    console.log('Settings state changed in context:', settings);
  }, [settings]);

  const loadSettings = async () => {
    try {
      console.log('Loading store settings...');
      setLoading(true);
      setError(null);

      // استخدام النظام المحسن لتحميل البيانات
      const storeSettings = await loadStoreSettingsReliably();
      console.log('Store settings loaded reliably:', storeSettings);

      setSettings(storeSettings);

    } catch (err) {
      console.error('Error loading store settings:', err);
      setError('حدث خطأ أثناء تحميل إعدادات المتجر');

      // في حالة الخطأ فقط، حاول تهيئة الإعدادات الافتراضية
      try {
        const defaultSettings = await initializeStoreSettings();
        setSettings(defaultSettings);
        setError(null); // إزالة الخطأ إذا نجحت التهيئة
      } catch (initErr) {
        console.error('Error initializing default settings:', initErr);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();

    // إعداد مراقبة التغييرات في الوقت الفعلي
    const unsubscribe = setupRealtimeStoreListener((updatedSettings) => {
      console.log('📡 تحديث البيانات من Firebase في الوقت الفعلي');
      setSettings(updatedSettings);
    });

    // تنظيف المراقبة عند إلغاء تحميل المكون
    return () => {
      unsubscribe();
    };
  }, []);

  const updateSettings = async (newSettings: StoreSettings) => {
    try {
      setLoading(true);
      setError(null);
      await updateStoreSettings(newSettings);
      cacheStoreData(newSettings); // تحديث التخزين المحلي فوراً
      setSettings(newSettings);
    } catch (err) {
      console.error('Error updating store settings:', err);
      setError('حدث خطأ أثناء تحديث إعدادات المتجر');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateSection = async (section: SettingsSection, data: any) => {
    console.log(`Updating section ${section} with data:`, data);
    if (!settings) {
      console.error('Cannot update section: settings is null');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const updatedSettings = {
        ...settings,
        [section]: data,
        lastUpdated: new Date()
      };

      console.log('Updated settings to save:', updatedSettings);
      await updateStoreSettings(updatedSettings);
      cacheStoreData(updatedSettings); // تحديث التخزين المحلي فوراً
      console.log('Settings saved successfully, updating state');
      setSettings(updatedSettings);
    } catch (err) {
      console.error(`Error updating ${section} settings:`, err);
      setError(`حدث خطأ أثناء تحديث إعدادات ${section}`);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateBanners = async (banners: Banner[]) => {
    if (!settings) return;

    try {
      setLoading(true);
      setError(null);

      const updatedSettings = {
        ...settings,
        display: {
          ...settings.display,
          banners
        },
        lastUpdated: new Date()
      };

      await updateStoreSettings(updatedSettings);
      setSettings(updatedSettings);
    } catch (err) {
      console.error('Error updating banners:', err);
      setError('حدث خطأ أثناء تحديث البانرات');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updatePromotions = async (promotions: Promotion[]) => {
    if (!settings) return;

    try {
      setLoading(true);
      setError(null);

      const updatedSettings = {
        ...settings,
        display: {
          ...settings.display,
          promotions
        },
        lastUpdated: new Date()
      };

      await updateStoreSettings(updatedSettings);
      setSettings(updatedSettings);
    } catch (err) {
      console.error('Error updating promotions:', err);
      setError('حدث خطأ أثناء تحديث الإعلانات الترويجية');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const refreshSettings = async () => {
    console.log('Refreshing store settings...');
    try {
      setLoading(true);
      setError(null);

      // تحميل الإعدادات من Firebase بدلاً من إعادة تهيئتها
      const storeSettings = await loadStoreSettingsReliably();
      console.log('Settings refreshed successfully:', storeSettings);

      setSettings(storeSettings);
      return storeSettings;
    } catch (err) {
      console.error('Error refreshing settings:', err);
      setError('حدث خطأ أثناء تحديث الإعدادات');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    settings,
    loading,
    error,
    updateSettings,
    updateSection,
    updateBanners,
    updatePromotions,
    refreshSettings
  };

  return (
    <StoreSettingsContext.Provider value={value}>
      {children}
    </StoreSettingsContext.Provider>
  );
};
